import { cn } from '@sohanemon/utils';
import { Iconify, Portal } from '@sohanemon/utils/components';
import { useMediaQuery, useScheduledEffect } from '@sohanemon/utils/hooks';
import { m } from '@sohanscript/i18n';
import { isRtl } from '@sohanscript/i18n/locale-meta';
import { Dock, DockIcon, DockItem } from '@sohanscript/ui/dock';
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@sohanscript/ui/tooltip';
import { ClientOnly } from '@tanstack/react-router';
import { useLenis } from 'lenis/react';
import {
	AnimatePresence,
	domAnimation,
	LazyMotion,
	type MotionValue,
	useScroll,
	useSpring,
} from 'motion/react';
import * as mMotion from 'motion/react-m';
import * as React from 'react';
import { Link, type LinkTo } from '@/components/link';

const HEXAGON_PATH = 'M40 4 L71.2 22 L71.2 58 L40 76 L8.8 58 L8.8 22 Z';
const ARROW_PATH = 'M12 4 L12 18 M6 13 L12 19 M18 13 L12 19';
const MENU_PATH = 'M6 7 L18 7 M6 12 L18 12 M6 17 L18 17';

type DockLinkProps = {
	href: string;
	icon: string;
	target?: string;
	label: string;
};

const HOME_LINK: DockLinkProps = {
	href: '/#top',
	icon: 'lucide:home',
	label: m.gt_home(),
};
const CONTACT_LINK: DockLinkProps = {
	href: '/start',
	icon: 'mage:message',
	label: m.gt_contact(),
};
type ScrollProgressRingProps = {
	progress: MotionValue<number>;
	atBottom: boolean;
};

function ScrollProgressRing({ progress, atBottom }: ScrollProgressRingProps) {
	return (
		<mMotion.svg
			className="pointer-events-none absolute -inset-1.5 overflow-visible"
			viewBox="0 0 80 80"
		>
			<mMotion.g
				animate={
					atBottom ? { scale: [1, 1.6], opacity: [0.5, 0] } : { opacity: 0 }
				}
				transition={
					atBottom
						? { duration: 1.4, ease: 'easeOut', repeat: Infinity }
						: { duration: 0.2 }
				}
				style={{ transformOrigin: '40px 40px', transformBox: 'fill-box' }}
			>
				<mMotion.path
					d={HEXAGON_PATH}
					fill="none"
					strokeWidth="3"
					strokeLinejoin="round"
					className="stroke-primary"
				/>
			</mMotion.g>
			<mMotion.path
				d={HEXAGON_PATH}
				fill="none"
				strokeWidth="3"
				strokeLinejoin="round"
				className="stroke-primary/20"
			/>
			<mMotion.path
				d={HEXAGON_PATH}
				fill="none"
				strokeWidth="3"
				strokeLinecap="round"
				strokeLinejoin="round"
				pathLength={progress}
				className="stroke-primary"
			/>
		</mMotion.svg>
	);
}

function ScrollTriggerIcon({
	className,
	isScrolledDown,
	isHovered,
	isMenuIcon,
	toggling = true,
}: {
	className?: string;
	isScrolledDown: boolean;
	isHovered?: boolean;
	isMenuIcon?: boolean;
	toggling?: boolean;
}) {
	const showMenu = toggling && !isHovered && isMenuIcon;
	return (
		<mMotion.svg
			viewBox="0 0 24 24"
			className={cn('size-5 overflow-visible', className)}
			initial={false}
			animate={{
				rotate: toggling
					? isHovered
						? isScrolledDown
							? 180
							: 0
						: isMenuIcon
							? 0
							: isScrolledDown
								? 180
								: 0
					: 180,
			}}
			transition={{ duration: 0.6, ease: 'easeInOut' }}
			style={{ transformOrigin: '50% 50%' }}
		>
			<mMotion.path
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
				initial={false}
				animate={{ d: showMenu ? MENU_PATH : ARROW_PATH }}
				transition={{ duration: 0.6, ease: 'easeInOut' }}
			/>
		</mMotion.svg>
	);
}

const SCROLL_DOWN_OFFSET = 700;

function useScrollDown(offset = SCROLL_DOWN_OFFSET) {
	const [isScrolledDown, setIsScrolledDown] = React.useState(false);
	const { scrollY, scrollYProgress } = useScroll();
	const progress = useSpring(scrollYProgress, {
		stiffness: 100,
		damping: 30,
		restDelta: 0.001,
	});

	useScheduledEffect(() => {
		return scrollY.on('change', (v) => setIsScrolledDown(v > offset));
	}, [scrollY]);

	return { isScrolledDown, progress, scrollY };
}

function MobileScrollTop() {
	const lenis = useLenis();
	const { isScrolledDown, progress } = useScrollDown();

	function handleClick() {
		if (lenis) lenis.scrollTo(0);
		else window.scrollTo({ top: 0, behavior: 'smooth' });
	}

	return (
		<AnimatePresence>
			{isScrolledDown && (
				<mMotion.div
					key="mobile-scroll-top"
					className={cn(
						'fixed right-5 bottom-6 isolate z-40 rtl:right-auto rtl:left-5',
					)}
					initial={{ opacity: 0, y: 12, scale: 0.8 }}
					animate={{ opacity: 1, y: 0, scale: 1 }}
					exit={{ opacity: 0, y: 12, scale: 0.8 }}
					transition={{ duration: 0.2, ease: 'easeOut' }}
				>
					<button
						type="button"
						onClick={handleClick}
						aria-label={m.gt_go_top()}
						className="grid aspect-square size-9 place-items-center text-primary active:scale-95"
					>
						<ScrollProgressRing progress={progress} atBottom={false} />
						<ScrollTriggerIcon
							className="size-4"
							isScrolledDown={false}
							toggling={false}
						/>
					</button>
				</mMotion.div>
			)}
		</AnimatePresence>
	);
}

function DesktopScrollDock({
	links,
	hideHomeLink,
	hideContactLink,
}: {
	links: DockLinkProps[];
	hideHomeLink: boolean;
	hideContactLink: boolean;
}) {
	const dockLinks = [
		...(hideHomeLink ? [] : [HOME_LINK]),
		...links,
		...(hideContactLink ? [] : [CONTACT_LINK]),
	];

	const lenis = useLenis();
	const { isScrolledDown, progress, scrollY } = useScrollDown();
	const [isHovered, setIsHovered] = React.useState(false);
	const [isMenuIcon, setIsMenuIcon] = React.useState(false);
	const [isAtBottom, setIsAtBottom] = React.useState(false);

	useScheduledEffect(() => {
		if (isHovered) {
			setIsMenuIcon(false);
			return;
		}
		const timer = setTimeout(
			() => setIsMenuIcon((v) => !v),
			3000 + Math.random() * 4000,
		);
		return () => clearTimeout(timer);
	}, [isHovered, isMenuIcon]);

	useScheduledEffect(() => {
		return scrollY.on('change', (v) => {
			setIsAtBottom(
				v > 0 && window.innerHeight + v >= document.body.scrollHeight - 80,
			);
		});
	}, [scrollY]);

	function handleClose() {
		setIsHovered(false);
	}

	function handleClick() {
		handleClose();
		const l = lenis;
		if (isScrolledDown) {
			if (l) l.scrollTo(0);
			else window.scrollTo({ top: 0, behavior: 'smooth' });
		} else {
			if (l) l.scrollTo(window.innerHeight, { duration: 3, lerp: 0.05 });
			else window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
		}
	}

	return (
		<>
			{/** biome-ignore lint/a11y/noStaticElementInteractions: hover-only wrapper around the dock */}
			<div
				className="fixed right-5 bottom-15 isolate z-60 rtl:right-auto rtl:left-5"
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
			>
				<Dock vertical visible={isHovered} className="items-center">
					{isHovered &&
						dockLinks.map((item, index) => {
							const [path = '.', hash] = item.href.split('#');
							return (
								<mMotion.div
									key={item.href}
									initial={{ opacity: 0, y: 48, scale: 0.6 }}
									animate={{ opacity: 1, y: 0, scale: 1 }}
									transition={{
										delay: (dockLinks.length - 1 - index) * 0.05,
										type: 'spring',
										stiffness: 260,
										damping: 20,
									}}
								>
									<Tooltip>
										<TooltipTrigger asChild>
											<Link
												to={path as LinkTo}
												hash={hash}
												target={item.target}
											>
												<DockItem
													id={hash}
													className="aspect-square rounded-full border-primary"
													onClick={handleClose}
												>
													<DockIcon className="flex items-center justify-center">
														<Iconify icon={item.icon} className="size-5" />
													</DockIcon>
												</DockItem>
											</Link>
										</TooltipTrigger>
										<TooltipContent
											side={isRtl() ? 'right' : 'left'}
											sideOffset={16}
										>
											{item.label ?? hash ?? path.replace(/^\./, '').slice(1)}
										</TooltipContent>
									</Tooltip>
								</mMotion.div>
							);
						})}

					<DockItem
						id="scroll-trigger"
						className="mt-2 aspect-square rounded-full border-primary bg-background"
						magnification={40}
						onClick={handleClick}
						aria-label={isScrolledDown ? m.gt_go_top() : m.gt_scroll_down()}
					>
						<ScrollProgressRing progress={progress} atBottom={isAtBottom} />
						<DockIcon className="relative flex items-center justify-center">
							<ScrollTriggerIcon
								isScrolledDown={isScrolledDown}
								isHovered={isHovered}
								isMenuIcon={isMenuIcon}
							/>
						</DockIcon>
					</DockItem>
				</Dock>
			</div>
		</>
	);
}

export function GoToTop({
	links = [],
	hideHomeLink = false,
	hideContactLink = false,
}: {
	links?: DockLinkProps[];
	hideHomeLink?: boolean;
	hideContactLink?: boolean;
}) {
	const isTouch = useMediaQuery('(pointer: coarse)');

	return (
		<ClientOnly>
			<LazyMotion features={domAnimation}>
				<Portal>
					{isTouch ? (
						<MobileScrollTop />
					) : (
						<DesktopScrollDock
							links={links}
							hideHomeLink={hideHomeLink}
							hideContactLink={hideContactLink}
						/>
					)}
				</Portal>
			</LazyMotion>
		</ClientOnly>
	);
}
